import "@shoelace-style/shoelace/dist/components/carousel/carousel.js";
import "@shoelace-style/shoelace/dist/components/carousel-item/carousel-item.js";

import { html, LitElement, PropertyValueMap } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { IMedia } from "../../models/Models";

import SlCarousel from "@shoelace-style/shoelace/dist/components/carousel/carousel.js";
import {
    getMediaTypeStringFromMimeType,
    getMediaTypeFromMimeType,
    MediaType,
    isGeoMimeType,
} from "../../Media";
import { watch } from "../internal/watch";
import { styles } from "./MediaCarousel.style";
import { litIf } from "../LitUtils";

import "./Media";
import { LightTheme } from "../ThemeLight";
import { MediaSize } from "./Media";

export type MediaCarouselItem = {
    media: IMedia;
    editable: boolean;
};

@customElement("ds-media-carousel")
export class MediaCarousel extends LitElement {
    static override styles = [LightTheme, styles];

    @property({ type: Array }) mediaList?: MediaCarouselItem[];
    @property({ type: Boolean }) autoplay = false;
    @property({ type: Number }) slidesPerPage = 1;
    @property({ type: Number }) slidesPerMove = 1;
    @property({ type: Number }) initialIndex?: number;
    @property({ type: Array }) filter?: string[];
    @property({ type: Boolean }) hideCloseButton = false;
    @property({ type: String }) size: MediaSize = "fullsize";

    @state() protected _filteredMediaList?: MediaCarouselItem[];
    @state() protected _loop = false;
    @state() protected _mouseDragging = true;

    @watch("mediaList")
    @watch("filter")
    _onMediaListChanged() {
        this._filteredMediaList = this._filterMedia(this.mediaList, this.filter);
    }

    protected override firstUpdated(
        _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
    ): void {
        this._loop = false;
        if (this._filteredMediaList) {
            if (this.initialIndex && this.mediaList && this.initialIndex < this.mediaList.length) {
                let index = this._filteredMediaList.indexOf(this.mediaList[this.initialIndex]);
                if (index === -1) {
                    index = 0;
                }
                this.goToSlide(index);
                this._loop = false;
            } else {
                this._loop = this._filteredMediaList.length > 1;
            }
            this._mouseDragging =
                getMediaTypeFromMimeType(this._filteredMediaList[0].media.mimeType) !==
                MediaType.Kml;
        }
    }

    public goToSlide(index: number) {
        try {
            (this.shadowRoot?.getElementById("carousel") as SlCarousel)?.goToSlide(index, "auto");
        } catch (_error) {
            // no -op
        }
    }

    private _filterMedia(mediaList: MediaCarouselItem[] | undefined, filter: string[] | undefined) {
        if (filter && mediaList) {
            const result = mediaList.filter((x) => {
                const type = getMediaTypeStringFromMimeType(x.media.mimeType);
                return type && filter.includes(type);
            });
            return result.length > 0 ? result : undefined;
        }
        return mediaList?.length ? mediaList : undefined;
    }

    override render() {
        return litIf(
            !!this._filteredMediaList,
            () =>
                html` <sl-carousel
                    id="carousel"
                    ?autoplay=${this.autoplay}
                    .pagination=${(this.mediaList?.length ?? 0) < 32}
                    navigation
                    ?mouse-dragging=${this._mouseDragging}
                    ?loop=${this._loop}
                    slides-per-page=${this.slidesPerPage}
                    slides-per-move=${this.slidesPerMove}
                    @sl-slide-change=${(event: CustomEvent) => {
                        // This is a bit of a hack. When viewing KML files we want the user
                        // to interact with the map so we need to disable mouse dragging on
                        // the carousel.
                        const item = event.detail.slide;
                        const index = item.dataset.mediaIndex as number;
                        if (this.mediaList && index < this.mediaList?.length) {
                            const item = this.mediaList[index];
                            this._mouseDragging = !isGeoMimeType(item.media.mimeType);
                        }
                    }}
                >
                    ${this._filteredMediaList?.map(
                        (x, index) =>
                            html`<sl-carousel-item data-media-index=${index}>
                                <ds-media
                                    .size=${this.size}
                                    .media=${x.media}
                                    ?showEditControls=${x.editable}
                                    ?hideCloseButton=${this.hideCloseButton}
                                ></ds-media>
                            </sl-carousel-item>`
                    )}v
                </sl-carousel>`
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-media-carousel": MediaCarousel;
    }
}
